import React, { useState, useEffect } from 'react'; // Added useState and useEffect
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@mui/material/styles'; // Added theme import
import { Box, Stack, Typography } from '@mui/material';

import generateUpdateMessage from 'src/utils/generateUpdateMessage';

export default function UpdateMessage({
  item,
  statusesList,
  departmentUsers,
  fetchUser,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [user, setUser] = useState({ id: '', username: '', role: '' });

  // Dynamically fetches the user details of the actor who performed the edit (via shared cache)
  useEffect(() => {
    if (item?.sender && fetchUser) {
      fetchUser(item.sender, (res) => {
        if (res) {
          setUser(res);
        }
      });
    }
  }, [item?.sender, fetchUser]);

  // Dynamic colors mapped based on active theme selection
  const statusColorMap = {
    success: isDark ? '#00A76F' : '#229A16', // Premium emerald in dark mode
    error: isDark ? '#FF4842' : '#B72136', // Premium red-error in dark mode
    warning: '#FFC107',
    info: isDark ? '#1890FF' : '#2065D1',
  };

  const priorityColorMap = {
    HIGH: isDark ? '#FF4842' : '#FF0000',
    MEDIUM: '#FFC107',
    LOW: isDark ? '#00A76F' : '#4CAF50',
  };

  const updates = generateUpdateMessage({
    message: item.message,
    statusesList,
    username: user?.username || '', // Binds the fetched username cleanly
    t,
    usersList: departmentUsers,
  });

  if (!updates || updates.length === 0) return null;

  const boldWithoutColor = (text) => {
    if (!text) return text;
    let parts = [text];

    // 1. Scan for User Names
    departmentUsers.forEach((u) => {
      const fullName = `${u.givenName} ${u.familyName}`.trim();
      const namesToSearch = [fullName, u.username].filter(Boolean);

      namesToSearch.forEach((name) => {
        const newParts = [];
        parts.forEach((p) => {
          if (typeof p === 'string') {
            const regex = new RegExp(`(${name})`, 'gi');
            const split = p.split(regex);
            for (let i = 0; i < split.length; i += 1) {
              if (split[i].toLowerCase() === name.toLowerCase()) {
                newParts.push(
                  <Box component="span" key={i + name} sx={{ fontWeight: 700 }}>
                    {split[i]}
                  </Box>
                );
              } else if (split[i] !== '') {
                newParts.push(split[i]);
              }
            }
          } else {
            newParts.push(p);
          }
        });
        parts = newParts;
      });
    });

    // 2. Scan for statuses
    statusesList.forEach((status) => {
      const translated = status.statusId <= 5 ? t(`status.${status.name}`) : status.name;
      const newParts = [];
      parts.forEach((part) => {
        if (typeof part === 'string') {
          const splitPart = part.split(translated);
          for (let i = 0; i < splitPart.length; i += 1) {
            newParts.push(splitPart[i]);
            if (i < splitPart.length - 1) {
              newParts.push(
                <Box component="span" key={i} sx={{ fontWeight: 700 }}>
                  {translated}
                </Box>
              );
            }
          }
        } else {
          newParts.push(part);
        }
      });
      parts = newParts;
    });

    // 3. Scan for priorities
    ['HIGH', 'MEDIUM', 'LOW'].forEach((prio) => {
      const translated = t(`priority.${prio}`);
      const newParts = [];
      parts.forEach((part) => {
        if (typeof part === 'string') {
          const splitPart = part.split(translated);
          for (let i = 0; i < splitPart.length; i += 1) {
            newParts.push(splitPart[i]);
            if (i < splitPart.length - 1) {
              newParts.push(
                <Box component="span" key={i} sx={{ fontWeight: 700 }}>
                  {translated}
                </Box>
              );
            }
          }
        } else {
          newParts.push(part);
        }
      });
      parts = newParts;
    });
    return parts;
  };

  const colorizeContent = (text) => {
    const cleanText = text.replace(/status\./g, '');

    const matches = [];

    // 1. Gather all status match positions
    statusesList.forEach((status) => {
      const translated = status.statusId <= 5 ? t(`status.${status.name}`) : status.name;
      let pos = cleanText.toLowerCase().indexOf(translated.toLowerCase());
      while (pos !== -1) {
        matches.push({
          start: pos,
          end: pos + translated.length,
          word: translated,
          color: statusColorMap[status.color] || status.color || '#919EAB',
        });
        pos = cleanText.toLowerCase().indexOf(translated.toLowerCase(), pos + 1);
      }
    });

    // 2. Gather all priority match positions
    ['HIGH', 'MEDIUM', 'LOW'].forEach((prio) => {
      const translated = t(`priority.${prio}`);
      let pos = cleanText.toLowerCase().indexOf(translated.toLowerCase());
      while (pos !== -1) {
        matches.push({
          start: pos,
          end: pos + translated.length,
          word: translated,
          color: priorityColorMap[prio],
        });
        pos = cleanText.toLowerCase().indexOf(translated.toLowerCase(), pos + 1);
      }
    });

    if (matches.length === 0) return boldWithoutColor(cleanText);

    // 3. Resolve Overlaps
    const validMatches = matches.filter((m1) => {
      const hasLongerOverlap = matches.some((m2) => {
        if (m1 === m2) return false;
        const overlap = m1.start < m2.end && m2.start < m1.end;
        return overlap && m2.end - m2.start > m1.end - m1.start;
      });
      return !hasLongerOverlap;
    });

    if (validMatches.length === 0) return boldWithoutColor(cleanText);

    // 4. Find the last remaining valid match
    let lastFound = validMatches[0];
    validMatches.forEach((m) => {
      if (m.start > lastFound.start) {
        lastFound = m;
      }
    });

    const beforeMatch = cleanText.substring(0, lastFound.start);
    const matchWord = cleanText.substring(lastFound.start, lastFound.end);
    const afterMatch = cleanText.substring(lastFound.end);

    return (
      <>
        {boldWithoutColor(beforeMatch)}
        <Box component="span" sx={{ color: lastFound.color, fontWeight: 700 }}>
          {matchWord}
        </Box>
        {boldWithoutColor(afterMatch)}
      </>
    );
  };

  return (
    <Box sx={{ width: '100%', my: 2.5, display: 'flex', justifyContent: 'center' }}>
      <Stack spacing={0.5} alignItems="center" sx={{ maxWidth: '85%' }}>
        {updates.map((text, index) => (
          <Typography
            key={index}
            sx={{
              fontSize: '0.8rem',
              fontWeight: 500,
              textAlign: 'center',
              color: 'text.secondary', // Mapped to theme secondary text
              lineHeight: 1.4,
            }}
          >
            {colorizeContent(text)}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

UpdateMessage.propTypes = {
  item: PropTypes.object.isRequired,
  statusesList: PropTypes.array,
  departmentUsers: PropTypes.array,
  fetchUser: PropTypes.func.isRequired,
};
